import { create } from 'zustand'
import type { TakeoffTool, TakeoffTrade } from '@/types/takeoff'

interface TakeoffEditorState {
  // Active tool
  activeTool: TakeoffTool
  setActiveTool: (tool: TakeoffTool) => void

  // Current trade for new measurements
  activeTrade: TakeoffTrade
  setActiveTrade: (trade: TakeoffTrade) => void

  // Active fixture type for count marks (null = generic count)
  activeCountTypeId: string | null
  setActiveCountTypeId: (id: string | null) => void

  // Show the fixture legend labels on the sheet
  showCountLabels: boolean
  toggleCountLabels: () => void

  // Current page
  currentPageNumber: number
  setCurrentPageNumber: (page: number) => void

  // Scale calibration mode
  isCalibrating: boolean
  calibrationPoints: Array<{ x: number; y: number }>
  startCalibration: () => void
  addCalibrationPoint: (point: { x: number; y: number }) => void
  cancelCalibration: () => void
  resetCalibration: () => void

  // Drawing state
  isDrawing: boolean
  drawingPoints: Array<{ x: number; y: number }>
  startDrawing: (point: { x: number; y: number }) => void
  addDrawingPoint: (point: { x: number; y: number }) => void
  finishDrawing: () => Array<{ x: number; y: number }>
  cancelDrawing: () => void

  // Selection
  selectedMeasurementId: string | null
  setSelectedMeasurementId: (id: string | null) => void

  // Zoom / pan
  zoom: number
  setZoom: (zoom: number) => void
  panOffset: { x: number; y: number }
  setPanOffset: (offset: { x: number; y: number }) => void

  // Undo stack (measurement IDs)
  undoStack: string[]
  pushUndo: (measurementId: string) => void
  popUndo: () => string | undefined
}

export const useTakeoffStore = create<TakeoffEditorState>((set, get) => ({
  activeTool: 'select',
  setActiveTool: (tool) => set({ activeTool: tool, isDrawing: false, drawingPoints: [] }),

  activeTrade: 'general',
  setActiveTrade: (trade) => set({ activeTrade: trade }),

  activeCountTypeId: null,
  // Picking a fixture type arms the count tool -- that is the only thing you
  // ever do with one.
  setActiveCountTypeId: (id) =>
    set((s) => ({
      activeCountTypeId: id,
      activeTool: id ? 'count' : s.activeTool,
      isDrawing: false,
      drawingPoints: [],
    })),

  showCountLabels: false,
  toggleCountLabels: () => set((s) => ({ showCountLabels: !s.showCountLabels })),

  currentPageNumber: 1,
  setCurrentPageNumber: (page) => set({ currentPageNumber: page }),

  isCalibrating: false,
  calibrationPoints: [],
  startCalibration: () => set({ isCalibrating: true, calibrationPoints: [], activeTool: 'calibrate' }),
  addCalibrationPoint: (point) =>
    set((s) => ({ calibrationPoints: [...s.calibrationPoints, point] })),
  cancelCalibration: () => set({ isCalibrating: false, calibrationPoints: [], activeTool: 'select' }),
  resetCalibration: () => set({ calibrationPoints: [] }),

  isDrawing: false,
  drawingPoints: [],
  startDrawing: (point) => set({ isDrawing: true, drawingPoints: [point] }),
  addDrawingPoint: (point) =>
    set((s) => ({ drawingPoints: [...s.drawingPoints, point] })),
  finishDrawing: () => {
    const points = get().drawingPoints
    set({ isDrawing: false, drawingPoints: [] })
    return points
  },
  cancelDrawing: () => set({ isDrawing: false, drawingPoints: [] }),

  selectedMeasurementId: null,
  setSelectedMeasurementId: (id) => set({ selectedMeasurementId: id }),

  zoom: 1,
  setZoom: (zoom) => set({ zoom: Math.max(0.1, Math.min(5, zoom)) }),
  panOffset: { x: 0, y: 0 },
  setPanOffset: (offset) => set({ panOffset: offset }),

  undoStack: [],
  pushUndo: (id) => set((s) => ({ undoStack: [...s.undoStack, id] })),
  popUndo: () => {
    const stack = get().undoStack
    if (stack.length === 0) return undefined
    const id = stack[stack.length - 1]
    set({ undoStack: stack.slice(0, -1) })
    return id
  },
}))
